import React from 'react';
import { useAnalytics } from '../../utils/analytics-provider/analytics-provider';
import { useBreakpoint } from '../../helpers/breakpoint-provider';
import {
  CarouselItem,
  VideoWrapper,
  VideoTitleCountWrapper,
  VideoTitle,
  VideoCount,
  VideoLearnMoreLink,
  CarouselWrapper
} from './styles';

/**
 * @typedef {Object} VideoTopic
 * @property {string} topic_url - URL path for the topic
 *
 *
 * @typedef {Object} VideoItem
 * @property {string} id - Unique identifier for the video
 * @property {string} youtube_id - YouTube video ID
 * @property {string} video_name - Display name of the video
 * @property {VideoTopic} [topic] - Associated topic information (optional)
 */

const getCarouselScrollSpeed = () => {
  const { isMobile, isTabletSm, tabletMd, isDesktop } = useBreakpoint();

  if (isMobile || isTabletSm) return 1;
  if (tabletMd) return 2;
  if (isDesktop) return 3;

  return 1;
};

/**
 * Validates the structure of the videos array
 * @param {any} videos - The videos array to validate
 * @returns {boolean} - Whether the videos array has a valid structure
 */
const validateVideosStructure = (videos) => {
  // Check if videos is an array
  if (!Array.isArray(videos)) {
    console.error('VideoCarousel: Expected videos prop to be an array, received:', typeof videos);
    return false;
  }

  // Check if array is empty
  if (videos.length === 0) {
    // This is not an error, just a warning
    console.warn('VideoCarousel: Received empty videos array');
    return true;
  }

  let isValid = true;

  // Check each video item
  videos.forEach((video, index) => {
    // Check if video has required properties directly or in its node property
    const hasDirectProperties = video && video.youtube_id && video.id && video.video_name;
    const hasNodeProperties = video && video.node && video.node.youtube_id && video.node.id && video.node.video_name;

    if (!hasDirectProperties && !hasNodeProperties) {
      console.error(`VideoCarousel: Video at index ${index} is missing required properties:`, video);
      isValid = false;
    }

    // If video has a topic, check its structure
    if (video.topic && typeof video.topic === 'object' && !video.topic.topic_url) {
      console.error(`VideoCarousel: Video at index ${index} has a topic without a 'topic_url' property:`, video.topic);
      isValid = false;
    }

    // If video has a node with a topic, check its structure
    if (video.node && video.node.topic && typeof video.node.topic === 'object' && !video.node.topic.topic_url) {
      console.error(`VideoCarousel: Video.node at index ${index} has a topic without a 'topic_url' property:`, video.node.topic);
      isValid = false;
    }
  });

  return isValid;
};

/**
 * Video Carousel Component
 * @param {Object} props - Component props
 * @param {VideoItem[]} props.videos - Array of video objects to display in the carousel
 * @param {boolean} [props.showMoreLink=true] - Whether to show "Learn more" links
 * @returns {JSX.Element} - Rendered component
 */
export const VideoCarousel = ({ videos, showMoreLink }) => {
  const { tabletMd, isDesktop } = useBreakpoint();
  const { trackEvent } = useAnalytics();

  // Validate videos structure
  validateVideosStructure(videos);

  // Filter videos that have a valid youtube_id either directly or in their node property
  const filteredVideos = videos?.filter(
    (vid) =>
      (vid?.youtube_id && vid?.youtube_id !== ' ') ||
      (vid?.node?.youtube_id && vid?.node?.youtube_id !== ' ')
  );
  const videoCount = filteredVideos?.length;

  const itemWidth = 337;
  const carouselHeight = showMoreLink ? 330 : 280;
  const arrowPlacement = {
    left: '-60px',
    right: '-60px',
    top: '110px'
  };

  const carouselItemStyle = {
    margin: '0 10px',
    height: showMoreLink ? '275px' : '245px',
    width: `${itemWidth - 20}px`
  };

  return (
    <CarouselWrapper
      key={new Date()}
      itemWidth={itemWidth}
      height={carouselHeight}
      arrowPlacement={arrowPlacement}
      showArrows={(tabletMd || isDesktop) && videoCount > 2}
      scrollSpeed={getCarouselScrollSpeed()}
    >
      {filteredVideos?.map((video, count) => {
        const node = video?.node || video;

        return (
          <CarouselItem key={node.id} style={carouselItemStyle}>
            <VideoWrapper youtubeId={node.youtube_id} />
            <VideoTitleCountWrapper showMoreLink={showMoreLink}>
              <VideoTitle>{node.video_name}</VideoTitle>
              <VideoCount>
                {count + 1}/{videoCount}
              </VideoCount>
            </VideoTitleCountWrapper>
            {showMoreLink && node?.topic?.topic_url && (
              <VideoLearnMoreLink
                to={`/${node?.topic?.topic_url}`}
                onClick={() =>
                  trackEvent('content_engagement_v2', {
                    engagement_type: 'learn_more_link_click',
                    link: node?.topic?.topic_url,
                    data: node
                  })
                }
              >
                Learn more
              </VideoLearnMoreLink>
            )}
          </CarouselItem>
        );
      })}
    </CarouselWrapper>
  );
};

VideoCarousel.defaultProps = {
  showMoreLink: true
};
